import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const TENSES = [
  { key: 'past', label: 'Past', icon: '\u23EA' },
  { key: 'present', label: 'Present', icon: '\u23FA' },
  { key: 'future', label: 'Future', icon: '\u23E9' }
];

const FORMS = [
  { key: 'positive', label: 'Positive', symbol: '+' },
  { key: 'negative', label: 'Negative', symbol: '\u2212' },
  { key: 'question', label: 'Question', symbol: '?' }
];

// Unique color scheme per category (matching HTML test page)
const CAT_STYLES = {
  all:           { bg: '#eef2ff', border: '#c7d2fe', shadow: 'rgba(99,102,241,0.15)', text: '#3730a3', badge: '#6366f1', activeBg: '#e0e7ff', activeBorder: '#6366f1', activeText: '#312e81' },
  movement:      { bg: '#eff6ff', border: '#bfdbfe', shadow: 'rgba(59,130,246,0.15)',  text: '#1e40af', badge: '#3b82f6', activeBg: '#dbeafe', activeBorder: '#3b82f6', activeText: '#1e3a8a' },
  food:          { bg: '#fef3c7', border: '#fcd34d', shadow: 'rgba(245,158,11,0.15)',  text: '#92400e', badge: '#f59e0b', activeBg: '#fde68a', activeBorder: '#f59e0b', activeText: '#78350f' },
  communication: { bg: '#fff7ed', border: '#fdba74', shadow: 'rgba(249,115,22,0.15)',  text: '#9a3412', badge: '#f97316', activeBg: '#ffedd5', activeBorder: '#f97316', activeText: '#7c2d12' },
  emotions:      { bg: '#fdf2f8', border: '#f9a8d4', shadow: 'rgba(236,72,153,0.15)',  text: '#9d174d', badge: '#ec4899', activeBg: '#fce7f3', activeBorder: '#ec4899', activeText: '#831843' },
  daily:         { bg: '#f0fdf4', border: '#86efac', shadow: 'rgba(34,197,94,0.15)',   text: '#166534', badge: '#22c55e', activeBg: '#dcfce7', activeBorder: '#22c55e', activeText: '#14532d' },
  work:          { bg: '#f8fafc', border: '#94a3b8', shadow: 'rgba(100,116,139,0.15)', text: '#334155', badge: '#64748b', activeBg: '#e2e8f0', activeBorder: '#475569', activeText: '#1e293b' },
  learning:      { bg: '#ecfeff', border: '#67e8f9', shadow: 'rgba(6,182,212,0.15)',   text: '#155e75', badge: '#06b6d4', activeBg: '#cffafe', activeBorder: '#06b6d4', activeText: '#164e63' },
  body:          { bg: '#fef2f2', border: '#fca5a5', shadow: 'rgba(239,68,68,0.15)',   text: '#991b1b', badge: '#ef4444', activeBg: '#fee2e2', activeBorder: '#ef4444', activeText: '#7f1d1d' },
  social:        { bg: '#f0fdfa', border: '#5eead4', shadow: 'rgba(20,184,166,0.15)',  text: '#115e59', badge: '#14b8a6', activeBg: '#ccfbf1', activeBorder: '#14b8a6', activeText: '#134e4a' },
  rest:          { bg: '#f5f3ff', border: '#c4b5fd', shadow: 'rgba(139,92,246,0.15)',  text: '#5b21b6', badge: '#8b5cf6', activeBg: '#ede9fe', activeBorder: '#8b5cf6', activeText: '#4c1d95' },
  other:         { bg: '#f8fafc', border: '#94a3b8', shadow: 'rgba(100,116,139,0.15)', text: '#334155', badge: '#64748b', activeBg: '#e2e8f0', activeBorder: '#475569', activeText: '#1e293b' },
};
// Dark mode overrides
const CAT_STYLES_DARK = {
  all:           { bg: '#1e1b4b', border: '#4338ca', shadow: 'rgba(99,102,241,0.25)', text: '#a5b4fc', badge: '#6366f1', activeBg: '#312e81', activeBorder: '#6366f1', activeText: '#c7d2fe' },
  movement:      { bg: '#172554', border: '#1d4ed8', shadow: 'rgba(59,130,246,0.25)',  text: '#93c5fd', badge: '#3b82f6', activeBg: '#1e3a8a', activeBorder: '#3b82f6', activeText: '#bfdbfe' },
  food:          { bg: '#451a03', border: '#b45309', shadow: 'rgba(245,158,11,0.25)',  text: '#fcd34d', badge: '#f59e0b', activeBg: '#78350f', activeBorder: '#f59e0b', activeText: '#fef3c7' },
  communication: { bg: '#431407', border: '#c2410c', shadow: 'rgba(249,115,22,0.25)',  text: '#fdba74', badge: '#f97316', activeBg: '#7c2d12', activeBorder: '#f97316', activeText: '#ffedd5' },
  emotions:      { bg: '#500724', border: '#be185d', shadow: 'rgba(236,72,153,0.25)',  text: '#f9a8d4', badge: '#ec4899', activeBg: '#831843', activeBorder: '#ec4899', activeText: '#fce7f3' },
  daily:         { bg: '#052e16', border: '#15803d', shadow: 'rgba(34,197,94,0.25)',   text: '#86efac', badge: '#22c55e', activeBg: '#14532d', activeBorder: '#22c55e', activeText: '#dcfce7' },
  work:          { bg: '#1e293b', border: '#475569', shadow: 'rgba(100,116,139,0.25)', text: '#94a3b8', badge: '#64748b', activeBg: '#334155', activeBorder: '#475569', activeText: '#e2e8f0' },
  learning:      { bg: '#083344', border: '#0e7490', shadow: 'rgba(6,182,212,0.25)',   text: '#67e8f9', badge: '#06b6d4', activeBg: '#164e63', activeBorder: '#06b6d4', activeText: '#cffafe' },
  body:          { bg: '#450a0a', border: '#b91c1c', shadow: 'rgba(239,68,68,0.25)',   text: '#fca5a5', badge: '#ef4444', activeBg: '#7f1d1d', activeBorder: '#ef4444', activeText: '#fee2e2' },
  social:        { bg: '#042f2e', border: '#0f766e', shadow: 'rgba(20,184,166,0.25)',  text: '#5eead4', badge: '#14b8a6', activeBg: '#134e4a', activeBorder: '#14b8a6', activeText: '#ccfbf1' },
  rest:          { bg: '#2e1065', border: '#7c3aed', shadow: 'rgba(139,92,246,0.25)',  text: '#c4b5fd', badge: '#8b5cf6', activeBg: '#4c1d95', activeBorder: '#8b5cf6', activeText: '#ede9fe' },
  other:         { bg: '#1e293b', border: '#475569', shadow: 'rgba(100,116,139,0.25)', text: '#94a3b8', badge: '#64748b', activeBg: '#334155', activeBorder: '#475569', activeText: '#e2e8f0' },
};

const getCatStyle = (slug, dark) => (dark ? CAT_STYLES_DARK : CAT_STYLES)[slug] || (dark ? CAT_STYLES_DARK : CAT_STYLES).other;

// Tailwind gradient classes for conjugation panel accent
const CONJ_GRADIENTS = {
  all: 'from-indigo-500 to-purple-500', movement: 'from-blue-500 to-cyan-500', food: 'from-orange-500 to-amber-500',
  communication: 'from-violet-500 to-purple-500', emotions: 'from-rose-500 to-pink-500', daily: 'from-emerald-500 to-teal-500',
  work: 'from-indigo-500 to-blue-500', learning: 'from-purple-500 to-fuchsia-500', body: 'from-teal-500 to-green-500',
  social: 'from-pink-500 to-rose-500', rest: 'from-violet-500 to-purple-500', other: 'from-gray-500 to-slate-500',
};
const getConjGradient = (slug) => CONJ_GRADIENTS[slug] || CONJ_GRADIENTS.other;

const VERBS_PER_PAGE = 8;

const TENSE_COLORS_LIGHT = { inactive: 'bg-gray-100 text-gray-500 hover:bg-gray-200' };
const TENSE_COLORS_DARK = { inactive: 'bg-gray-700/50 text-gray-400 hover:bg-gray-600' };
const TENSE_ACTIVE = {
  past: 'from-blue-500 to-indigo-600',
  present: 'from-emerald-500 to-teal-600',
  future: 'from-purple-500 to-fuchsia-600',
};
const FORM_COLORS = { positive: 'from-emerald-500 to-green-600', negative: 'from-red-500 to-rose-600', question: 'from-amber-500 to-orange-600' };

const ConjugationContent = ({ selectedTense, setSelectedTense, selectedForm, setSelectedForm, selectedCategory, loadingConj, conjugations, isDarkMode, copiedIdx, onCopy }) => {
  const { t } = useTranslation();
  const inactiveClass = isDarkMode ? TENSE_COLORS_DARK.inactive : TENSE_COLORS_LIGHT.inactive;
  return (
    <div className="px-3 sm:px-4 py-3 space-y-2.5">
      <div className="flex gap-1.5">
        {TENSES.map(tense => (
          <button key={tense.key} onClick={() => setSelectedTense(tense.key)}
            className={`flex-1 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 ${
              selectedTense === tense.key ? `bg-gradient-to-r ${TENSE_ACTIVE[tense.key]} text-white shadow-md` : inactiveClass
            }`}><span className="mr-0.5">{tense.icon}</span> {t('verb_tense_' + tense.key)}</button>
        ))}
      </div>
      <div className="flex gap-1.5">
        {FORMS.map(f => (
          <button key={f.key} onClick={() => setSelectedForm(f.key)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
              selectedForm === f.key ? `bg-gradient-to-r ${FORM_COLORS[f.key]} text-white shadow-sm` : inactiveClass
            }`}><span className="font-bold mr-0.5">{f.symbol}</span> {t('verb_form_' + f.key)}</button>
        ))}
      </div>
      {loadingConj && (!conjugations || conjugations.length === 0) ? (
        <div className="flex justify-center py-6"><div className="space-y-2 w-full">
          <div className="h-1 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
            <div className={`h-full rounded-full bg-gradient-to-r ${getConjGradient(selectedCategory)}`} style={{ animation: 'scan 1.2s ease-in-out infinite', width: '40%' }} />
          </div>
          <p className={`text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Loading...</p>
        </div></div>
      ) : conjugations && conjugations.length > 0 ? (
        <div className="rounded-xl overflow-hidden border transition-opacity duration-200" style={{ userSelect: 'none', WebkitUserSelect: 'none', opacity: loadingConj ? 0.5 : 1 }}>
          <div className={`divide-y ${isDarkMode ? 'divide-gray-700/50' : 'divide-gray-100'}`} style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
            {conjugations.map((conj, idx) => (
              <div key={idx} className={`px-3 py-2.5 flex items-center gap-2 ${isDarkMode ? `${idx % 2 === 0 ? 'bg-gray-800/30' : 'bg-transparent'} hover:bg-gray-700/40` : `${idx % 2 === 0 ? 'bg-gray-50/50' : 'bg-transparent'} hover:bg-indigo-50/40`} transition-colors`}>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {conj.english}
                    {conj.gender && conj.number && (
                      <span className={`ml-1.5 text-[10px] px-1 py-0.5 rounded-md font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        {conj.number === 'singular' ? 'S' : 'P'}{conj.gender === 'male' ? 'M' : conj.gender === 'female' ? 'F' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{conj.latin || '\u2014'}</span>
                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} dir="rtl">{conj.arabic || '\u2014'}</span>
                  </div>
                </div>
                <button onClick={() => onCopy(conj, idx)}
                  className={`p-1.5 rounded-lg flex-shrink-0 transition-all duration-200 ${copiedIdx === idx ? isDarkMode ? 'text-green-400 bg-green-900/30 scale-110' : 'text-green-600 bg-green-50 scale-110' : isDarkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`} title={t('copy')}>
                  {copiedIdx === idx ? '\u2713' : '\uD83D\uDCCB'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : conjugations && conjugations.length === 0 ? (
        <div className={`text-center py-6 rounded-xl border ${isDarkMode ? 'bg-gray-700/20 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
          <span className="text-2xl block mb-2">{'\uD83D\uDCDD'}</span>
          <span className="text-xs">{t('verb_no_conjugations')}</span>
        </div>
      ) : null}
    </div>
  );
};

const KEYFRAMES = `
@keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes scan { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }
@keyframes gentle-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes ph-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(168,85,247,0.3); transform: scale(1); } 50% { box-shadow: 0 0 0 15px rgba(168,85,247,0); transform: scale(1.05); } }
@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
`;

const VerbExplorer = ({ API, showNotification, isDarkMode, userToken, setCurrentView, setInitialProfileTab }) => {
  const { t } = useTranslation();

  // App-shell caches: read synchronously so categories + 'all' verbs render
  // from frame 1. Cached verbs only for 'all' category to keep storage bounded.
  const _readCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedCategories = _readCache('verbCategoriesCache');
  const _cachedSettings = _readCache('verbSettingsCache');
  const _cachedAllVerbs = _readCache('verbVerbsAllCache');

  const [categories, setCategories] = useState(_cachedCategories || []);
  const [verbs, setVerbs] = useState(_cachedAllVerbs || []);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVerb, setSelectedVerb] = useState(null);
  const [selectedTense, setSelectedTense] = useState('past');
  const [selectedForm, setSelectedForm] = useState('positive');
  const [conjugations, setConjugations] = useState(null);
  const [baseInfo, setBaseInfo] = useState({ latin: '', arabic: '' });
  const [loading, setLoading] = useState(false);
  const [loadingConj, setLoadingConj] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [settings, setSettings] = useState(_cachedSettings || { beta_badge_visible: true, beta_badge_text: 'Beta', is_pro_only: false });
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [pronounGender, setPronounGender] = useState('he');
  const [showCount, setShowCount] = useState(VERBS_PER_PAGE);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const conjRef = useRef(null);
  // Init true if we have cached verbs, so the loading spinner doesn't replace cached cards
  const hasLoadedRef = useRef(!!_cachedAllVerbs);
  const baseUrl = API.replace('/api', '');

  useEffect(() => { setShowCount(VERBS_PER_PAGE); }, [selectedCategory, searchQuery]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (selectedVerb && isMobile) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [selectedVerb, isMobile]);

  useEffect(() => {
    if (mobileCatOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileCatOpen]);

  const headers = useMemo(() => ({ 'Authorization': `Bearer ${userToken}`, 'Content-Type': 'application/json' }), [userToken]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API}/verbs/categories`, { headers });
        if (res.ok) {
          const data = await res.json();
          const cats = data.categories || [];
          setCategories(cats);
          try { localStorage.setItem('verbCategoriesCache', JSON.stringify(cats)); } catch {}
          if (data.settings) {
            setSettings(data.settings);
            try { localStorage.setItem('verbSettingsCache', JSON.stringify(data.settings)); } catch {}
          }
        }
      } catch (e) { console.error('Failed to fetch categories', e); }
    };
    if (userToken) fetchCategories();
  }, [API, userToken]);

  const fetchVerbs = useCallback(async (cat) => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cat && cat !== 'all') params.append('category', cat);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      const res = await fetch(`${API}/verbs?${params.toString()}`, { headers });
      if (res.status === 403) {
        setShowProModal(true);
      } else if (res.ok) {
        const data = await res.json();
        const list = data.verbs || [];
        setVerbs(list);
        // Only cache the 'all' category with no search query (the default landing state)
        if ((!cat || cat === 'all') && !searchQuery.trim()) {
          try { localStorage.setItem('verbVerbsAllCache', JSON.stringify(list)); } catch {}
        }
        hasLoadedRef.current = true;
      }
    } catch (e) { console.error('Failed to fetch verbs', e); }
    setLoading(false);
  }, [API, headers, searchQuery]);

  useEffect(() => {
    if (userToken) fetchVerbs(selectedCategory);
  }, [selectedCategory, userToken, fetchVerbs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (userToken) fetchVerbs(selectedCategory);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, userToken, fetchVerbs]);

  const fetchConjugations = useCallback(async () => {
    if (!selectedVerb) return;
    setLoadingConj(true);
    try {
      const res = await fetch(
        `${API}/verbs/${encodeURIComponent(selectedVerb)}/conjugations?tense=${selectedTense}&form=${selectedForm}`,
        { headers }
      );
      if (res.status === 403) { setShowProModal(true); }
      else if (res.status === 429) { showNotification(t('verb_rate_limit'), 'error'); }
      else if (res.ok) {
        const data = await res.json();
        setConjugations(data.conjugations || []);
        setBaseInfo({ latin: data.base_latin || '', arabic: data.base_arabic || '' });
      }
    } catch (e) { console.error('Failed to fetch conjugations', e); }
    setLoadingConj(false);
  }, [API, userToken, selectedVerb, selectedTense, selectedForm]);

  useEffect(() => {
    if (selectedVerb) fetchConjugations();
  }, [selectedVerb, selectedTense, selectedForm, fetchConjugations]);

  const handleVerbClick = (verb) => {
    if (!verb.accessible) { setShowProModal(true); return; }
    setSelectedVerb(verb.name);
    setSelectedTense('past');
    setSelectedForm('positive');
  };

  const handleCopy = async (conj, idx) => {
    const text = `${conj.english} = ${conj.latin} / ${conj.arabic}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch { showNotification(t('verb_copy_failed'), 'error'); }
  };

  const visibleVerbs = verbs.slice(0, showCount);
  const hasMore = verbs.length > showCount;
  const remaining = verbs.length - showCount;

  const getVerbImage = (verb) => {
    if (pronounGender === 'she' && verb.she_image) return verb.she_image;
    if (pronounGender === 'he' && verb.he_image) return verb.he_image;
    return verb.he_image || verb.she_image || '';
  };

  const conjProps = { selectedTense, setSelectedTense, selectedForm, setSelectedForm, selectedCategory, loadingConj, conjugations, isDarkMode, copiedIdx, onCopy: handleCopy };

  return (
    <div className={isDarkMode ? 'text-white' : 'text-gray-900'}
      onContextMenu={e => e.preventDefault()}
      onDragStart={e => e.preventDefault()}
      style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
    <div className={`max-w-[1400px] mx-auto px-3 sm:px-5 pb-20 lg:pb-8 transition-all duration-300`}
      style={showProModal ? { filter: 'blur(6px) grayscale(0.5) brightness(0.7)', pointerEvents: 'none', userSelect: 'none' } : undefined}>

      {/* ── Announcement / Beta Badge (top note) ── */}
      {settings.beta_badge_visible && (
        <div className={`text-center py-2 mb-2`}>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${
            isDarkMode ? 'bg-amber-900/20 border-amber-700 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}>
            {settings.beta_badge_text || 'Beta'}{settings.beta_badge_description ? ` \u2014 ${settings.beta_badge_description}` : ''}
          </span>
        </div>
      )}

      {/* ── Header (centered, gradient text like HTML) ── */}
      <header className="text-center pt-4 sm:pt-6 pb-3" style={{ background: isDarkMode ? 'transparent' : 'linear-gradient(180deg, rgba(99,102,241,0.04) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-center gap-3 mb-1">
          {settings.page_icon_url
            ? <img src={`${baseUrl}${settings.page_icon_url}`} alt="" className="w-9 h-9 sm:w-10 sm:h-10 object-contain" />
            : null}
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{
            background: isDarkMode ? 'linear-gradient(135deg, #818cf8, #a78bfa, #f472b6)' : 'linear-gradient(135deg, #4f46e5, #7c3aed, #db2777)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
          }}>
            {t('verb_explorer_title')}
          </h1>
        </div>
        <p className={`text-sm sm:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('verb_explorer_subtitle')}
        </p>
      </header>

      {/* ── Controls: He/She Toggle (centered, pill shape) ── */}
      <div className="flex flex-wrap justify-center items-center gap-4 mb-3">
        <div className={`inline-flex rounded-full overflow-hidden border-2 ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
        }`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <button onClick={() => setPronounGender('he')}
            className="px-5 sm:px-7 py-2.5 text-sm font-semibold transition-all duration-300"
            style={pronounGender === 'he'
              ? { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', borderRadius: '999px' }
              : { background: 'transparent', color: isDarkMode ? '#9ca3af' : '#64748b' }
            }>{t('he_pronoun')} / Howa</button>
          <button onClick={() => setPronounGender('she')}
            className="px-5 sm:px-7 py-2.5 text-sm font-semibold transition-all duration-300"
            style={pronounGender === 'she'
              ? { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', borderRadius: '999px' }
              : { background: 'transparent', color: isDarkMode ? '#9ca3af' : '#64748b' }
            }>{t('she_pronoun')} / Hiya</button>
        </div>
      </div>

      {/* ── Category Bar (Desktop: colorful cards / Mobile: trigger button) ── */}
      {!isMobile ? (
        <div className="flex flex-wrap justify-center gap-2.5 px-4 py-3 mb-2">
          {/* "All" category card */}
          {(() => {
            const cs = getCatStyle('all', isDarkMode);
            const isActive = selectedCategory === 'all';
            return (
              <button key="all" onClick={() => setSelectedCategory('all')}
                className="relative flex flex-col items-center gap-1.5 rounded-2xl cursor-pointer transition-all duration-300 min-w-[100px]"
                style={{
                  padding: '14px 12px 10px',
                  background: isActive ? cs.activeBg : cs.bg,
                  border: `2px solid ${isActive ? cs.activeBorder : cs.border}`,
                  boxShadow: `0 ${isActive ? '6px 28px' : '2px 10px'} ${cs.shadow}`,
                  transform: isActive ? 'translateY(-4px) scale(1.08)' : undefined,
                }}>
                <span className="absolute -top-1.5 -right-1.5 text-[10px] font-extrabold text-white rounded-full min-w-[22px] text-center px-1.5 py-0.5"
                  style={{ background: cs.badge, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
                  {categories.reduce((sum, c) => sum + (c.verb_count || 0), 0)}
                </span>
                {settings.all_icon_url
                  ? <img src={`${baseUrl}${settings.all_icon_url}`} alt="" className="w-8 h-8 object-contain" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                  : <span className="text-2xl" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>{'\uD83D\uDCDA'}</span>}
                <span className="text-xs font-bold text-center whitespace-nowrap" style={{ color: isActive ? cs.activeText : cs.text }}>{t('all')}</span>
              </button>
            );
          })()}
          {categories.map(cat => {
            const cs = getCatStyle(cat.slug, isDarkMode);
            const isActive = selectedCategory === cat.slug;
            return (
              <button key={cat.id} onClick={() => setSelectedCategory(cat.slug)}
                className="relative flex flex-col items-center gap-1.5 rounded-2xl cursor-pointer transition-all duration-300 min-w-[100px]"
                style={{
                  padding: '14px 12px 10px',
                  background: isActive ? cs.activeBg : cs.bg,
                  border: `2px solid ${isActive ? cs.activeBorder : cs.border}`,
                  boxShadow: `0 ${isActive ? '6px 28px' : '2px 10px'} ${cs.shadow}`,
                  transform: isActive ? 'translateY(-4px) scale(1.08)' : undefined,
                }}>
                <span className="absolute -top-1.5 -right-1.5 text-[10px] font-extrabold text-white rounded-full min-w-[22px] text-center px-1.5 py-0.5"
                  style={{ background: cs.badge, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
                  {cat.verb_count || 0}
                </span>
                {cat.icon_url
                  ? <img src={`${baseUrl}${cat.icon_url}`} alt="" className="w-8 h-8 object-contain" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                  : <span className="text-2xl" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>{cat.icon}</span>}
                <span className="text-xs font-bold text-center whitespace-nowrap truncate max-w-[80px]" style={{ color: isActive ? cs.activeText : cs.text }}>{cat.name}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <button onClick={() => setMobileCatOpen(true)}
          className={`w-full flex items-center justify-between px-4 py-3 mb-3 rounded-2xl border-2 transition-all ${
            isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-800'
          }`} style={{ boxShadow: '0 2px 12px rgba(99,102,241,0.1)' }}>
          <div className="flex items-center gap-3">
            {(() => {
              if (selectedCategory === 'all') return settings.all_icon_url
                ? <img src={`${baseUrl}${settings.all_icon_url}`} alt="" className="w-7 h-7 object-contain" />
                : <span className="text-xl">{'\uD83D\uDCDA'}</span>;
              const cat = categories.find(c => c.slug === selectedCategory);
              return cat?.icon_url
                ? <img src={`${baseUrl}${cat.icon_url}`} alt="" className="w-7 h-7 object-contain" />
                : <span className="text-xl">{cat?.icon || '\uD83D\uDCDA'}</span>;
            })()}
            <span className="font-bold text-sm" style={{ color: isDarkMode ? '#a5b4fc' : '#4f46e5' }}>
              {selectedCategory === 'all' ? t('verb_all_categories') : categories.find(c => c.slug === selectedCategory)?.name || t('all')}
            </span>
          </div>
          <span className="text-xs text-gray-400">{'\u25B6'}</span>
        </button>
      )}

      {/* ── Search (centered, pill shape, below categories) ── */}
      <div className="flex justify-center mb-5 px-2">
        <div className="relative w-full max-w-xs sm:max-w-sm">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input type="text" placeholder={t('verb_search_placeholder')} value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-full text-sm border-2 outline-none transition-all ${
              isDarkMode
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-purple-500'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500'
            }`}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
          />
        </div>
      </div>

      {/* ── Verb Card Grid + Conjugation Panel ── */}
      <div className={`flex flex-col ${selectedVerb && !isMobile ? 'lg:flex-row' : ''} gap-5`}>
        <div className={`${selectedVerb && !isMobile ? 'w-full lg:w-[60%]' : 'w-full'}`}>
          {loading && verbs.length === 0 ? (
            /* Skeleton — only when no cached verbs. If we have cached verbs, keep them visible while background refresh runs. */
            <div className="animate-pulse" style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${selectedVerb && !isMobile ? '220px' : '260px'}, 1fr))`,
              gap: '20px'
            }}>
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className={`rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} style={{ height: '320px' }} />
              ))}
            </div>
          ) : verbs.length === 0 ? (
            <div className={`text-center py-10 rounded-2xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <span className="text-3xl block mb-2">{searchQuery ? '\uD83D\uDD0D' : '\uD83D\uDCED'}</span>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                {searchQuery ? t('verb_no_match') : t('verb_no_verbs_category')}
              </p>
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${selectedVerb && !isMobile ? '220px' : '260px'}, 1fr))`,
                gap: '20px'
              }}>
                {visibleVerbs.map((verb, idx) => {
                  const isSelected = selectedVerb === verb.name;
                  const img = getVerbImage(verb);
                  return (
                    <div key={verb.name} onClick={() => handleVerbClick(verb)}
                      style={{
                        transition: 'box-shadow 0.3s, border-color 0.3s',
                        borderRadius: '20px', padding: '20px',
                        border: `1px solid ${isSelected ? 'rgba(139,92,246,0.5)' : isDarkMode ? '#374151' : '#e8ecf1'}`,
                        background: isDarkMode ? '#1f2937' : 'white',
                        boxShadow: isSelected ? '0 8px 32px rgba(139,92,246,0.15)' : '0 2px 12px rgba(0,0,0,0.04)',
                        cursor: verb.accessible ? 'pointer' : 'default',
                      }}
                      className={`transition-all ${verb.accessible ? 'hover:shadow-xl' : 'opacity-60'}`}
                    >
                      {/* Image area */}
                      <div style={{
                        height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '14px', marginBottom: '16px', overflow: 'hidden', position: 'relative',
                        background: isDarkMode ? 'linear-gradient(180deg, #111827, #1f2937)' : 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
                      }}>
                        {img ? (
                          <>
                            <img src={`${baseUrl}${img}`} alt=""
                              width="200" height="200"
                              loading={idx < 12 ? 'eager' : 'lazy'}
                              decoding="async"
                              style={{
                                maxHeight: '200px', maxWidth: '100%', objectFit: 'contain',
                                animation: 'gentle-float 3s ease-in-out infinite',
                                animationDelay: `${(idx % 5) * 0.3}s`,
                                filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.08))',
                                WebkitUserDrag: 'none', userSelect: 'none', pointerEvents: 'none',
                              }}
                              onContextMenu={e => e.preventDefault()}
                              onDragStart={e => e.preventDefault()}
                              onError={e => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                            />
                            {/* Hidden fallback placeholder — shown on image load error */}
                            <div style={{
                              display: 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                              gap: '10px', width: '100%', height: '100%',
                              background: isDarkMode
                                ? 'linear-gradient(180deg, #2e1065, #1e1b4b)'
                                : 'linear-gradient(180deg, #fdf4ff, #fae8ff)',
                              border: isDarkMode ? '3px dotted #7c3aed' : '3px dotted #d8b4fe',
                              borderRadius: '14px',
                            }}>
                              <div style={{
                                width: '80px', height: '80px', borderRadius: '50%',
                                background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                animation: 'ph-pulse 2s ease-in-out infinite',
                                boxShadow: '0 0 0 0 rgba(168,85,247,0.3)',
                              }}>
                                <span style={{ fontSize: '2rem', fontWeight: 900, color: 'white' }}>
                                  {verb.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <span style={{
                                fontSize: '0.8rem', fontWeight: 700, color: '#7c3aed',
                                textTransform: 'uppercase', letterSpacing: '2px',
                              }}>{verb.name}</span>
                            </div>
                          </>
                        ) : (
                          /* Pulsing circle placeholder (same as HTML test page) */
                          <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            gap: '10px', width: '100%', height: '100%',
                            background: isDarkMode
                              ? 'linear-gradient(180deg, #2e1065, #1e1b4b)'
                              : 'linear-gradient(180deg, #fdf4ff, #fae8ff)',
                            border: isDarkMode ? '3px dotted #7c3aed' : '3px dotted #d8b4fe',
                            borderRadius: '14px',
                          }}>
                            <div style={{
                              width: '80px', height: '80px', borderRadius: '50%',
                              background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              animation: 'ph-pulse 2s ease-in-out infinite',
                              boxShadow: '0 0 0 0 rgba(168,85,247,0.3)',
                            }}>
                              <span style={{ fontSize: '2rem', fontWeight: 900, color: 'white' }}>
                                {verb.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span style={{
                              fontSize: '0.8rem', fontWeight: 700, color: '#7c3aed',
                              textTransform: 'uppercase', letterSpacing: '2px',
                            }}>{verb.name}</span>
                          </div>
                        )}
                        {!verb.accessible && (
                          <div style={{
                            position: 'absolute', top: '8px', right: '8px',
                            fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                            background: 'rgba(0,0,0,0.3)', color: 'white', backdropFilter: 'blur(4px)',
                          }}>{'\uD83D\uDD12'} {t('verb_pro_badge')}</div>
                        )}
                      </div>
                      {/* Verb text */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: '1.2rem', fontWeight: 800, textTransform: 'uppercase',
                          letterSpacing: '2px', color: isDarkMode ? '#e2e8f0' : '#1e293b',
                          marginBottom: '2px',
                        }}>{verb.name}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Show More */}
              {hasMore && (
                <div className="flex justify-center mt-6">
                  <button onClick={() => setShowCount(prev => prev + VERBS_PER_PAGE)}
                    style={{
                      padding: '10px 28px', borderRadius: '999px', fontSize: '0.9rem',
                      fontWeight: 700, cursor: 'pointer', transition: 'all 0.3s',
                      border: isDarkMode ? '2px solid #4b5563' : '2px solid #e2e8f0',
                      background: isDarkMode ? '#1f2937' : 'white',
                      color: isDarkMode ? '#d1d5db' : '#64748b',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    }}>
                    {t('verb_show_more_remaining').replace('{n}', remaining)}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Desktop Conjugation Panel ── */}
        {selectedVerb && !isMobile && (
          <div ref={conjRef} className={`w-full lg:w-[40%] lg:sticky lg:top-4 lg:self-start rounded-2xl border overflow-hidden shadow-lg ${isDarkMode ? 'bg-gray-800/90 border-gray-700' : 'bg-white border-gray-200'}`}
            style={{ animation: 'fadeInUp 0.3s ease-out' }}>
            <div className={`h-1 bg-gradient-to-r ${getConjGradient(selectedCategory)}`} />
            <div className={`px-4 py-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} border-b`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                  <h2 className={`text-base sm:text-lg font-bold capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedVerb}</h2>
                  {baseInfo.latin && (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${isDarkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>{baseInfo.latin}</span>
                      {baseInfo.arabic && <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`} dir="rtl">{baseInfo.arabic}</span>}
                    </div>
                  )}
                </div>
                <button onClick={() => setSelectedVerb(null)}
                  className={`p-1.5 rounded-xl transition-colors flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                  title={t('verb_close_panel')}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <ConjugationContent {...conjProps} />
          </div>
        )}
      </div>

      <style>{KEYFRAMES}</style>
    </div>

    {/* ── Mobile Bottom Sheet (Conjugations) ── */}
    {selectedVerb && isMobile && (
      <>
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
          onClick={() => setSelectedVerb(null)} style={{ animation: 'fadeIn 0.2s ease-out' }} />
        <div className={`fixed inset-x-0 z-50 rounded-t-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-white border-t border-gray-200'}`}
          style={{ bottom: '3.5rem', maxHeight: 'calc(85vh - 3.5rem)', animation: 'slideUp 0.3s ease-out' }}>
          <div className="flex justify-center pt-2.5 pb-1">
            <div className={`w-10 h-1 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
          </div>
          <div className={`h-1 bg-gradient-to-r ${getConjGradient(selectedCategory)}`} />
          <div className={`px-4 py-3 flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} border-b`}>
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <h2 className={`text-base font-bold capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedVerb}</h2>
              {baseInfo.latin && (
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${isDarkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>{baseInfo.latin}</span>
                  {baseInfo.arabic && <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`} dir="rtl">{baseInfo.arabic}</span>}
                </div>
              )}
            </div>
            <button onClick={() => setSelectedVerb(null)}
              className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(85vh - 3.5rem - 100px)' }}>
            <ConjugationContent {...conjProps} />
          </div>
        </div>
      </>
    )}

    {/* ── Mobile Category Slide Panel ── */}
    {mobileCatOpen && (
      <>
        <div className="fixed inset-0 z-[998] bg-black/40"
          onClick={() => setMobileCatOpen(false)} style={{ animation: 'fadeIn 0.2s ease-out' }} />
        <div className={`fixed top-0 right-0 bottom-0 z-[999] w-[300px] max-w-[85vw] overflow-y-auto ${
          isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-b from-gray-50 to-white'
        }`} style={{ boxShadow: '-4px 0 30px rgba(0,0,0,0.15)', animation: 'slideInRight 0.3s cubic-bezier(0.4,0,0.2,1)' }}>
          <div className={`sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-inherit border-gray-200'}`}>
            <h3 className={`text-base font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('verb_categories')}</h3>
            <button onClick={() => setMobileCatOpen(false)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-base ${isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              ✕
            </button>
          </div>
          <div className="px-4 py-3 space-y-1.5">
            {/* "All" item */}
            {(() => {
              const cs = getCatStyle('all', isDarkMode);
              const isActive = selectedCategory === 'all';
              return (
                <button onClick={() => { setSelectedCategory('all'); setMobileCatOpen(false); }}
                  className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-left transition-all"
                  style={{
                    background: isActive ? cs.activeBg : isDarkMode ? 'transparent' : 'white',
                    border: `1.5px solid ${isActive ? cs.activeBorder : 'transparent'}`,
                  }}>
                  {settings.all_icon_url
                    ? <img src={`${baseUrl}${settings.all_icon_url}`} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    : <span className="text-2xl flex-shrink-0">{'\uD83D\uDCDA'}</span>}
                  <div className="flex-1">
                    <div className="text-sm font-bold" style={{ color: isActive ? cs.activeText : isDarkMode ? '#e2e8f0' : '#1e293b' }}>{t('all')}</div>
                    <div className="text-xs" style={{ color: '#94a3b8' }}>{categories.reduce((sum, c) => sum + (c.verb_count || 0), 0)} {t('verb_count_suffix')}</div>
                  </div>
                  <span className="text-base" style={{ color: cs.badge, opacity: isActive ? 1 : 0 }}>{'\u2713'}</span>
                </button>
              );
            })()}
            {categories.map(cat => {
              const cs = getCatStyle(cat.slug, isDarkMode);
              const isActive = selectedCategory === cat.slug;
              return (
                <button key={cat.id} onClick={() => { setSelectedCategory(cat.slug); setMobileCatOpen(false); }}
                  className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-left transition-all"
                  style={{
                    background: isActive ? cs.activeBg : isDarkMode ? 'transparent' : 'white',
                    border: `1.5px solid ${isActive ? cs.activeBorder : 'transparent'}`,
                  }}>
                  {cat.icon_url
                    ? <img src={`${baseUrl}${cat.icon_url}`} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    : <span className="text-2xl flex-shrink-0">{cat.icon}</span>}
                  <div className="flex-1">
                    <div className="text-sm font-bold" style={{ color: isActive ? cs.activeText : isDarkMode ? '#e2e8f0' : '#1e293b' }}>{cat.name}</div>
                    <div className="text-xs" style={{ color: '#94a3b8' }}>{cat.verb_count || 0} {t('verb_count_suffix')}</div>
                  </div>
                  <span className="text-base" style={{ color: cs.badge, opacity: isActive ? 1 : 0 }}>{'\u2713'}</span>
                </button>
              );
            })}
          </div>
        </div>
      </>
    )}

    {/* ── Pro Upgrade Modal ── */}
    {showProModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className={`rounded-2xl p-6 sm:p-8 max-w-sm w-full shadow-2xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
          style={{ animation: 'fadeInUp 0.3s ease-out' }}>
          <div className="text-center mb-5">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg mb-3" style={{ animation: 'pulse 2s ease-in-out infinite' }}>
              <span className="text-3xl">{'\uD83D\uDD13'}</span>
            </div>
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('verb_unlock_all')}</h3>
            <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('verb_upgrade_msg').replace('{n}', String(verbs.length > 0 ? verbs.length : 194))}
            </p>
          </div>
          <div className="flex gap-2.5">
            <button onClick={() => setShowProModal(false)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {t('maybe_later')}
            </button>
            <button onClick={() => { setShowProModal(false); setInitialProfileTab('membership'); setCurrentView('profile'); }}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-md hover:shadow-lg transition-all hover:scale-[1.02]">
              {t('verb_upgrade_now')}
            </button>
          </div>
        </div>
      </div>
    )}

    </div>
  );
};

export default VerbExplorer;
